import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { catchError, filter, map, Subject, takeUntil, tap, throwError } from 'rxjs';
import { AadUser } from '../model/aad-user.model';
import { UserService } from './user.service';

@Injectable({
	providedIn: 'root',
})
export class AuthenticationService implements OnDestroy {
	private readonly destroy$: Subject<void> = new Subject<void>();

	constructor(
		private readonly msalService: MsalService,
		private readonly userService: UserService,
		private readonly msalBroadcastService: MsalBroadcastService,
		private readonly router: Router
	) {}

	public initializeAuthentication(): void {
		this.handleAuthenticationEvents();
		this.handleInteractionStatus();
	}

	public getMsalClaims(): AadUser {
		const msalClaims = this.msalService.instance.getActiveAccount()?.idTokenClaims;
		if (!msalClaims) {
			throw new Error('msalClaims is not available.');
		}

		return msalClaims as unknown as AadUser;
	}

	public handleRedirect(): void {
		this.msalService
			.handleRedirectObservable()
			.pipe(
				tap(() => {
					if (this.router.url.includes('/auth')) {
						this.router.navigate(['/']);
					}
				}),
				catchError((error) => throwError(() => new Error(`MSAL redirect error: ${error}`))),
				takeUntil(this.destroy$)
			)
			.subscribe();
	}

	private handleAuthenticationEvents(): void {
		this.msalBroadcastService.msalSubject$
			.pipe(
				filter(
					(msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
				),
				takeUntil(this.destroy$)
			)
			.subscribe({
				next: (result: EventMessage) => {
					const payload = result.payload as AuthenticationResult;
					this.msalService.instance.setActiveAccount(payload.account);
				},
				error: (error) => throwError(() => error),
			});
	}

	private handleInteractionStatus(): void {
		this.msalBroadcastService.inProgress$
			.pipe(
				filter((status: InteractionStatus) => status === InteractionStatus.None),
				tap(() => this.ensureActiveAccount()),
				map(() => this.msalService.instance.getActiveAccount()),
				filter((account): account is AccountInfo => !!account),
				map(() => this.getMsalClaims()),
				tap((claims) => this.userService.updateRtUser(claims)),
				catchError((error) => throwError(() => new Error(`Error fetching data: ${error}`))),
				takeUntil(this.destroy$)
			)
			.subscribe();
	}

	private ensureActiveAccount(): void {
		const activeAccount = this.msalService.instance.getActiveAccount();
		if (!activeAccount) {
			const accounts = this.msalService.instance.getAllAccounts();
			if (accounts.length > 0) {
				this.msalService.instance.setActiveAccount(accounts[0]);
			}
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
